import { Box, Button, Collapse, TextField } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useDebounce } from '../hooks/useDebounce';

interface FilterBarProps {
  search: string;
  onSearch: (value: string) => void;
  searchLabel?: string;
  searchPlaceholder?: string;
  filtersOpen?: boolean;
  onToggleFilters?: () => void;
  activeCount?: number;
  onClear?: () => void;
  /** Keep keystrokes in this field. The page hears the text only after this delay. */
  debounceMs?: number;
  /** Remount the field when the page clears the search from outside. */
  searchResetKey?: number;
  children?: ReactNode;
}

function DraftSearch({
  initial,
  delay,
  placeholder,
  label,
  onCommit,
}: {
  initial: string;
  delay: number;
  placeholder?: string;
  label: string;
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState(initial);
  const debounced = useDebounce(draft, delay);
  const sent = useRef(initial);
  useEffect(() => {
    if (debounced === sent.current) return;
    sent.current = debounced;
    onCommit(debounced);
  }, [debounced, onCommit]);
  return (
    <TextField
      size="small"
      label={label}
      placeholder={placeholder}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      sx={{ flex: '1 1 240px', minWidth: 200 }}
    />
  );
}

export default function FilterBar({
  search,
  onSearch,
  searchLabel = 'Suche',
  searchPlaceholder,
  filtersOpen = false,
  onToggleFilters,
  activeCount = 0,
  onClear,
  debounceMs,
  searchResetKey = 0,
  children,
}: FilterBarProps) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {debounceMs != null ? (
          <DraftSearch
            key={searchResetKey}
            initial={search}
            delay={debounceMs}
            placeholder={searchPlaceholder}
            label={searchLabel}
            onCommit={onSearch}
          />
        ) : (
          <TextField
            size="small"
            label={searchLabel}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            sx={{ flex: '1 1 240px', minWidth: 200 }}
          />
        )}
        {children && onToggleFilters && (
          <Button variant="outlined" onClick={onToggleFilters}>
            Filter{activeCount > 0 ? ` (${activeCount})` : ''}
          </Button>
        )}
        {activeCount > 0 && onClear && (
          <Button onClick={onClear} color="inherit">
            Zurücksetzen
          </Button>
        )}
      </Box>
      {children && (
        <Collapse in={filtersOpen}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 1.5,
              mt: 2,
            }}
          >
            {children}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
